import { useEffect, useState } from "react";
import { MessageSquareQuote } from "lucide-react";
import { theme, card, type as text } from "../theme";
import { StatusTag } from "./Drawing";
import { PortalLink } from "./PortalLink";
import { api } from "../api/client";
import { fmtDateOnly } from "../lib/dateOnly";

// Your audit interview briefing (docs/AUDITOR-TOOLS.md "Interview briefings", backend lib/interviewBriefing.ts). Before a
// certification audit, what an auditor is likely to ask you about the records and controls you own, and what to have
// ready. Built from your own records; only you see it (GET /me/tasks/briefing serves the caller's own and no one else's).

interface Item { label: string; href: string; ask: string[]; ready: string[]; flags: string[] }
interface Briefing {
  audit: { date: string; daysAway: number; standards: string[]; source: "booked" | "target"; bureauName: string | null };
  records: Item[];
  controls: Array<Item & { controlId: string; status: string }>;
  toFix: string[];
}

export function AuditBriefing() {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get<{ briefing: Briefing | null }>("/me/tasks/briefing").then((r) => setBriefing(r.briefing)).catch(() => setBriefing(null)); }, []);
  if (!briefing) return null;
  const a = briefing.audit;
  const items = [...briefing.controls, ...briefing.records];
  return (
    <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ ...text.sectionTitle, margin: 0, display: "flex", gap: 6, alignItems: "center" }}><MessageSquareQuote size={15} /> Your audit briefing</div>
        <StatusTag tone={briefing.toFix.length ? "partial" : "met"}>{briefing.toFix.length ? `${briefing.toFix.length} to sort out` : "Ready"}</StatusTag>
      </div>
      <div style={{ ...text.hint, marginTop: 4 }}>
        {a.standards.join(", ")} {a.source === "booked" ? `audit${a.bureauName ? ` by ${a.bureauName}` : ""}` : "target audit date"} on {fmtDateOnly(a.date)}, in {a.daysAway} day{a.daysAway === 1 ? "" : "s"}.
        {" "}What you are likely to be asked about the {items.length} thing{items.length === 1 ? "" : "s"} you own, and what to have ready. Only you can see this.
      </div>
      {briefing.toFix.length > 0 && (
        <ul style={{ ...text.body, margin: "8px 0 0", paddingLeft: 18 }}>
          {briefing.toFix.slice(0, 5).map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ background: "none", border: "none", padding: 0, marginTop: 8, cursor: "pointer", ...text.hint, color: theme.color.accentText }}>
        {open ? "Hide the questions" : "Show the likely questions"}
      </button>
      {open && (
        <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
          {items.map((it) => (
            <div key={it.href + it.label} style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 8 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <PortalLink to={it.href} style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>{it.label}</PortalLink>
                {it.flags.map((f) => <StatusTag key={f} tone="partial">{f}</StatusTag>)}
              </div>
              <div style={{ ...text.label, marginTop: 4 }}>Likely to be asked</div>
              <ul style={{ ...text.hint, margin: "2px 0 0", paddingLeft: 18 }}>{it.ask.map((q) => <li key={q}>{q}</li>)}</ul>
              <div style={{ ...text.label, marginTop: 4 }}>Have ready</div>
              <ul style={{ ...text.hint, margin: "2px 0 0", paddingLeft: 18 }}>{it.ready.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
